import React, { FunctionComponent, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  MdOutlineError,
  MdOutlineInfo,
  MdOutlineQuestionMark,
  MdOutlineWarning,
} from "react-icons/md";
import {
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import { useQuery } from "@apollo/client";
import { LIST_SITES_QUERY } from "graphql/queries";
import { ListSites } from "graphql/generated";

import { useAllSitesData } from "common/all-sites-data";
import { useUserPreference } from "common/user-preferences";
import { RelativeTimestamp } from "common/hooks/time";

import MuteButton from "./MuteButton";
import { groupName } from "scenes/site/scenes/alarms/AlarmTypes";


export interface IAlarmRuleNotification {
  alarm: IAlarmRuleNotificationAlarmType;
  rule_id: string;
  rule_name: string;
  severity: IAlarmTypeLevel;
  site_id: string;
  triggered_at: string;
}

interface IAlarmRuleNotificationAlarmType {
  abbr: string;
  alarm_type_id: string;
  condition_id: string;
  level: IAlarmTypeLevel;
  message: string;
  name: string;
  namespace: string;
  severity: number;
  start_time: string;
}

enum IAlarmTypeLevel {
  Error = 'ERROR',
  Info = 'INFO',
  Warning = 'WARNING',
}

interface IProps {
  alert: IAlarmRuleNotification;
}


const Alert: FunctionComponent<IProps> = ({
  alert,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [ mutedAlerts ] = useUserPreference<boolean>("ui__muted_alerts");
  const { data: sitesData } = useQuery<ListSites>(LIST_SITES_QUERY);
  const { alertsData } = useAllSitesData();
  const [ isOpen, setOpen ] = useState<boolean>(true);
  const textColor = useColorModeValue("black", "white");
  const bgColor = useColorModeValue("white", "black");
  const warningColor = useColorModeValue("yellow.300", "yellow.600");
  const errorColor = useColorModeValue("red.300", "red.600");
  const infoColor = useColorModeValue("blue.300", "blue.600");
  const noColor = useColorModeValue("gray.300", "gray.600");
  const itemLevelColor = useMemo(() => {
    switch(alert.alarm.level.toUpperCase()) {
      case "ERROR": return errorColor;
      case "WARNING": return warningColor;
      case "INFO": return infoColor;
      default: return noColor;
    }
  }, [alert.alarm.level, errorColor, warningColor, infoColor, noColor]);
  const itemLevelIcon = useMemo(() => {
    switch(alert.alarm.level.toUpperCase()) {
      case "ERROR": return MdOutlineError;
      case "WARNING": return MdOutlineWarning;
      case "INFO": return MdOutlineInfo;
      default: return MdOutlineQuestionMark;
    }
  }, [alert.alarm.level]);

  useEffect(() => {
    setOpen(!!alertsData);
  }, [alertsData]);

  return (
    <>
      <Modal
        isOpen={isOpen && !mutedAlerts}
        onClose={() => setOpen(false)}
        size="2xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay
          bg={itemLevelColor}
          opacity={.5}
          // backdropFilter="auto"
          // backdropInvert="100%"
          // backdropBlur="2px"
        />
        <ModalContent
          borderRadius={0}
        >
          <ModalCloseButton
            top={3}
            size="lg"
            title={intl.formatMessage({
              id: "alarms__alerts__close",
              defaultMessage: "Close alarm alert",
            })}
          />
          <MuteButton
            size="lg"
            bg="transparent"
            position="absolute"
            top={3}
            left={3}
          />

          <ModalBody
            p={12}
            cursor="pointer"
            onClick={() => {
              navigate(`/site/${alert.site_id}/alarms/${alert.alarm.alarm_type_id}`);
              setOpen(false);
            }}
          >
            <Stack
              direction="column"
              gap={3}
              alignItems="center"
            >
              <Icon as={itemLevelIcon} boxSize={20} mb={-4} />

              <Stack
                direction="row"
              >
                <Text fontWeight="bold">
                  {groupName(intl, alert.alarm.namespace)}:
                </Text>
                <Text fontWeight="bold">
                  {alert.alarm.name}
                </Text>
                <Text fontWeight="bold">
                  ({alert.alarm.abbr})
                </Text>
              </Stack>

              <Text fontSize="4xl">
                {alert.alarm.message}
              </Text>

              <Stack
                direction="row"
                gap={3}
                alignItems="center"
                bg={textColor}
                color={bgColor}
                px={3}
                py={1}
                fontSize="sm"
                alignSelf="stretch"
                justifyContent="space-between"
              >

                <Text fontWeight="bold" textTransform="uppercase">
                  {sitesData?.listSites.find(_ => _.id === alert.site_id)?.name}
                </Text>

                <Text>
                  <RelativeTimestamp date={`${alert.alarm.start_time}+00:00`} />
                </Text>

              </Stack>

            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Alert;
